import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Modal, ModalContent, ModalTrigger, useModalContext } from '@fiverr-private/overlay';
import { raiseToast } from '@fiverr-private/toast';
import { reportClickedOnRequestUpgradeTrigger } from '../../../events/requestUpgradeEvents';
import { useGetAdminData } from '../useGetMembersList';
import { AdminMember } from '../../../types';
import { appTranslate as t } from '../../../utils/i18n';
import { RequestType } from '../types';
import { SuccessMessage } from './SuccessMessage';
import { Content } from './Content';

export interface RequestUpgradeModalProps {
    children: (props: childrenProps) => React.ReactElement;
    onSuccess: () => void;
    requestType?: RequestType;
    initialAdminMemberData?: AdminMember;
    queryClient?: QueryClient;
    isDisabled?: boolean;
    initialIsOpen?: boolean;
    /* ### BI Data ### */
    sellerId?: number;
    upgradeSource?: string;
    pageName: string;
    currentPlan: 'pro_essential' | 'pro_advanced';
}

interface RequestUpgradeModalContentProps
    extends Omit<RequestUpgradeModalProps, 'children' | 'queryClient' | 'isDisabled'> {
    requestType: RequestType;
}

interface childrenProps {
    isLoading: boolean;
    isDisabled: boolean;
}

export const RequestUpgradeModalContent = ({
    onSuccess,
    requestType,
    initialAdminMemberData,
    ...rest
}: RequestUpgradeModalContentProps) => {
    const [shouldShowSuccessMessage, setShouldShowSuccessMessage] = useState(false);
    const { isOpen } = useModalContext();
    const { admin, isError } = useGetAdminData(isOpen, initialAdminMemberData);

    useEffect(() => {
        if (isError) raiseToast({ message: t('request_upgrade_modal.error'), location: 'top', type: 'error' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError]);

    if (!isOpen) {
        return null;
    }

    if (!admin) return null;

    const onSuccessfulRequest = () => {
        setShouldShowSuccessMessage(true);
        onSuccess();
    };

    return (
        <ModalContent onAnimationEnd={() => setShouldShowSuccessMessage(false)}>
            {shouldShowSuccessMessage ? (
                <SuccessMessage admin={admin} />
            ) : (
                <Content admin={admin} onSuccessfulRequest={onSuccessfulRequest} requestType={requestType} {...rest} />
            )}
        </ModalContent>
    );
};

export const RequestUpgradeModal = ({
    children,
    onSuccess,
    requestType = 'upgrade',
    initialAdminMemberData,
    queryClient: _queryClient,
    isDisabled = false,
    initialIsOpen = false,
    ...rest
}: RequestUpgradeModalProps) => {
    const [open, isOpen] = useState(initialIsOpen);
    const { currentPlan, pageName } = rest;
    const { isError, isAdminDataLoading: isLoading } = useGetAdminData(false, initialAdminMemberData);

    const [queryClient] = React.useState(() => _queryClient || new QueryClient());

    const trigger = children({ isLoading, isDisabled });

    const openModal = () => {
        reportClickedOnRequestUpgradeTrigger({ pageName, currentPlan });

        if (isError) {
            raiseToast({ message: t('request_upgrade_modal.error'), location: 'top', type: 'error' });
            return;
        }

        isOpen(true);
    };

    return (
        <QueryClientProvider client={queryClient}>
            <Modal isOpen={open} onOpen={openModal} onClose={() => isOpen(false)} fullScreenBreakpoint="none">
                <ModalTrigger isDisabled={isDisabled}>{trigger}</ModalTrigger>
                <RequestUpgradeModalContent
                    initialAdminMemberData={initialAdminMemberData}
                    onSuccess={onSuccess}
                    requestType={requestType}
                    {...rest}
                />
            </Modal>
        </QueryClientProvider>
    );
};
